// react
import { useMemo, useRef, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Alert, Box, Button, Chip, Divider, Stack, Tooltip, Typography } from '@mui/material';

// icons
import CheckIcon from '@mui/icons-material/Check';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import UploadFileIcon from '@mui/icons-material/UploadFile';

// types
import {
	FieldResponse,
	SectionResponse,
	AiDocument,
	Condition,
	ContractV2,
	Invoice,
	InvoiceAttachment,
	Voucher,
	VerificationSection,
} from '../../../../@types';

// cards
import { CardInvoiceDetails } from '../../../cards/CardInvoiceDetails/CardInvoiceDetails';

// fields
import { Input } from '../../../fields/Input/Input';
import { Select } from '../../../fields/Select/Select';
import { SelectCurrency } from '../../../fields/SelectCurrency/SelectCurrency';
import { UploadDoc } from '../../../fields/UploadDoc/UploadDoc';
import { YesNo } from '../../../fields/YesNo/YesNo';

// services
import { uploadFieldDocument, uploadMilestoneInvoice } from '../../../../services/documents';
import { submitMilestoneResponses } from '../../../../services/milestones';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { formatDate } from '../../../../utils/dates';
import { validateField } from '../../../../utils/fields';

const CONDITION_STATUS_LABEL: Record<string, string> = {
	PENDING: 'Awaiting Verification',
	SUBMITTED: 'Submitted',
	VERIFIED: 'Verified',
	APPROVED: 'Approved',
	REJECTED: 'Rejected',
	DEACTIVATED: 'Inactive',
};

const formatStatusLabel = (status: string | null | undefined): string => {
	if (!status) return '';
	const upper = status.toUpperCase();
	if (CONDITION_STATUS_LABEL[upper]) return CONDITION_STATUS_LABEL[upper];
	return upper.charAt(0) + upper.slice(1).toLowerCase();
};

export const TERMINAL_STATUSES = new Set(['VERIFIED', 'APPROVED', 'COMPLETED']);

export const isTerminal = (status: string | null | undefined): boolean =>
	!!status && TERMINAL_STATUSES.has(status.toUpperCase());

interface FieldLocalState {
	value: unknown;
	documentIds: string[];
	documentNames: string[];
}

const fieldKey = (sectionId: string, fieldId: string) => `${sectionId}::${fieldId}`;

// props
export interface MilestoneProps {
	contract: ContractV2;
	currency: string;
	index: number;
	isGated: boolean;
	isPaid: boolean;
	linkedVouchers: Voucher[];
	milestone: Condition;
	onChanged: (result?: { draft?: boolean }) => void;
	onError: (message?: string) => void;
	supplierEntityId: number | undefined;
	invoices?: Invoice[];
	blobUrlByDocId?: Map<string, string>;
	documents?: AiDocument[];
}

export const Milestone = ({
	contract,
	currency,
	index,
	isGated,
	isPaid,
	linkedVouchers,
	milestone,
	onChanged,
	onError,
	supplierEntityId,
	invoices,
	blobUrlByDocId,
	documents,
}: MilestoneProps) => {
	const { t } = useTranslation();

	const sections: VerificationSection[] = useMemo(
		() => milestone.verificationSections || [],
		[milestone.verificationSections]
	);

	// Hydrate the local field state from any existing voucher
	// section_responses so previously-saved drafts repopulate on load.
	const initialFieldState = useMemo(() => {
		const map = new Map<string, FieldLocalState>();
		const existing = linkedVouchers
			.map((v) => (v as Voucher & { sectionResponses?: SectionResponse[] }).sectionResponses)
			.find((sr): sr is SectionResponse[] => Array.isArray(sr));
		if (existing) {
			existing.forEach((sr) => {
				(sr.fieldResponses || []).forEach((fr) => {
					map.set(fieldKey(sr.sectionId, fr.fieldId), {
						value: fr.value ?? '',
						documentIds: fr.documentIds || (fr.documentId ? [fr.documentId] : []),
						documentNames: fr.documentNames || (fr.documentName ? [fr.documentName] : []),
					});
				});
			});
		}
		return map;
	}, [linkedVouchers]);

	const [fieldState, setFieldState] = useState<Map<string, FieldLocalState>>(initialFieldState);
	const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(new Map());
	const [isSaving, setIsSaving] = useState(false);

	// Milestone-level invoice — the invoice file is part of the milestone
	// form and is uploaded together with the submission (see doSubmit),
	// creating the linked invoice record.
	const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
	// Native file input; reset via the ref after submit so re-selecting the
	// same file still fires onChange.
	const invoiceInputRef = useRef<HTMLInputElement>(null);
	// Invoice metadata collected alongside the file so the created invoice
	// record — and the linked voucher — carry the supplier's stated number,
	// date, amount and currency instead of null.
	const [invoiceNumber, setInvoiceNumber] = useState('');
	const [invoiceDate, setInvoiceDate] = useState('');
	const [invoiceAmount, setInvoiceAmount] = useState('');
	const [invoiceCurrency, setInvoiceCurrency] = useState(currency);
	const [invoiceErrors, setInvoiceErrors] = useState<{
		number?: boolean;
		date?: boolean;
		amount?: boolean;
	}>({});

	const contractActive = (contract.status || '').toUpperCase() === 'ACTIVE';
	const terminal = isTerminal(milestone.status) || isPaid;
	// Once the linked voucher leaves its initial GENERATED state the PIU
	// has begun review (or it's been cancelled). Either way the supplier
	// loses edit rights even if the milestone itself is not yet terminal.
	// Server-side guard `assertVoucherEditableBySupplier` enforces this.
	const voucherLocked = linkedVouchers.some(
		(v) => v.status && v.status.toUpperCase() !== 'GENERATED'
	);
	// `disabled` blocks interaction (inputs, upload, save buttons). Sequencing,
	// the per-card terminal state, and contract-not-ACTIVE all collapse to the
	// same UI affordance.
	const disabled = (isGated && !terminal) || !contractActive;
	const readOnly = terminal || voucherLocked;

	// Are there any fields on this milestone the supplier can actually act on?
	// Internal-only fields (allowSupplierCompletion !== true) render as
	// locked rows the WBG team will fill in later — if a milestone has none
	// of the supplier-fillable kind, hiding Save Draft / Submit Milestone
	// avoids implying there's anything to do here.
	const hasSupplierActionableFields = useMemo(
		() =>
			sections.some((section) => section.fields.some((f) => f.allowSupplierCompletion === true)),
		[sections]
	);

	const updateField = (sectionId: string, fieldId: string, patch: Partial<FieldLocalState>) => {
		setFieldState((prev) => {
			const next = new Map(prev);
			const key = fieldKey(sectionId, fieldId);
			const current = next.get(key) || { value: '', documentIds: [], documentNames: [] };
			next.set(key, { ...current, ...patch });
			return next;
		});
	};

	const buildSectionResponses = (): SectionResponse[] =>
		sections.map((section) => ({
			sectionId: section.id,
			sectionName: section.name,
			conditionId: milestone.id,
			// Default — submitMilestoneResponses() overwrites with PENDING/SUBMITTED
			// based on the supplier's Save Draft vs Submit Milestone intent.
			status: 'PENDING',
			fieldResponses: section.fields
				.filter((f) => f.allowSupplierCompletion === true)
				.map<FieldResponse>((f) => {
					const state = fieldState.get(fieldKey(section.id, f.id)) || {
						value: '',
						documentIds: [],
						documentNames: [],
					};
					const docIds = state.documentIds;
					const docNames = state.documentNames;
					return {
						fieldId: f.id,
						label: f.label,
						fieldType: f.fieldType,
						value:
							f.fieldType === 'document' ? null : state.value == null ? null : String(state.value),
						documentIds: f.fieldType === 'document' ? docIds : undefined,
						documentNames: f.fieldType === 'document' ? docNames : undefined,
						documentId: f.fieldType === 'document' && docIds.length === 1 ? docIds[0] : undefined,
						documentName:
							f.fieldType === 'document' && docNames.length === 1 ? docNames[0] : undefined,
					};
				}),
		}));

	const runValidation = (): { ok: boolean; errors: Map<string, string> } => {
		const errors = new Map<string, string>();
		sections.forEach((section) => {
			section.fields
				.filter((f) => f.allowSupplierCompletion === true)
				.forEach((f) => {
					const state = fieldState.get(fieldKey(section.id, f.id));
					const err = validateField(
						f,
						state?.value,
						f.fieldType === 'document' ? state?.documentIds : undefined
					);
					if (err) errors.set(fieldKey(section.id, f.id), err);
				});
		});
		return { ok: errors.size === 0, errors };
	};

	const voucherId = linkedVouchers[0]?.id ?? null;

	const doSubmit = async (draft: boolean) => {
		const invoiceFile = invoiceFiles[0];
		if (!draft) {
			const { ok, errors } = runValidation();
			setFieldErrors(errors);
			// When the supplier attaches an invoice file, its number, date and
			// amount become required so the created invoice record — and the
			// linked voucher — carry real values instead of null.
			const invErr: { number?: boolean; date?: boolean; amount?: boolean } = {};
			if (invoiceFile) {
				if (!invoiceNumber.trim()) invErr.number = true;
				if (!invoiceDate) invErr.date = true;
				const amt = Number(invoiceAmount);
				if (invoiceAmount === '' || Number.isNaN(amt) || amt <= 0) invErr.amount = true;
			}
			setInvoiceErrors(invErr);
			if (!ok || Object.keys(invErr).length > 0) {
				onError(t('requiredField'));
				return;
			}
		} else {
			setFieldErrors(new Map());
			setInvoiceErrors({});
		}

		setIsSaving(true);
		try {
			// The invoice is part of the form, but only finalized on Submit (never
			// Save Draft) and only when the supplier selected a file that isn't
			// already on this milestone. Re-submitting the form (or re-picking the
			// same file) must not spawn a duplicate invoice record.
			const alreadyUploaded =
				!draft && invoiceFile
					? milestoneInvoices.some((inv) =>
							(inv.attachments || []).some(
								(att) => att.name === invoiceFile.name && att.size === invoiceFile.size
							)
						)
					: false;
			const submittingInvoice = !draft && Boolean(invoiceFile) && !alreadyUploaded;
			const parsedInvoiceAmount = invoiceAmount === '' ? null : Number(invoiceAmount);
			const resolvedCurrency = invoiceCurrency || currency;
			const invoiceMeta = submittingInvoice
				? {
						invoiceNumber: invoiceNumber.trim() || null,
						invoiceDate: invoiceDate || null,
						invoiceAmount: parsedInvoiceAmount,
						invoiceCurrency: resolvedCurrency,
					}
				: undefined;

			const sectionResponses = buildSectionResponses();
			// Send when there are field responses to save, or when we need to stamp
			// the invoice metadata onto the linked voucher.
			if (sectionResponses.some((s) => s.fieldResponses.length > 0) || invoiceMeta) {
				await submitMilestoneResponses(milestone.id, sectionResponses, {
					draft,
					voucherId,
					invoice: invoiceMeta,
				});
			}

			// Create the invoice record from the attached file + metadata.
			if (submittingInvoice && invoiceFile) {
				await uploadMilestoneInvoice({
					contractId: contract.id,
					milestoneNumber: milestone.id,
					milestoneName: milestone.name,
					invoiceFile,
					projectId: contract.project,
					supplierEntityId,
					supplierName,
					currency: resolvedCurrency,
					amount: parsedInvoiceAmount ?? amountValue,
					invoiceNumber: invoiceNumber.trim() || null,
					invoiceDate: invoiceDate || null,
				});
			}

			if (submittingInvoice) {
				setInvoiceFiles([]);
				setInvoiceNumber('');
				setInvoiceDate('');
				setInvoiceAmount('');
				setInvoiceCurrency(currency);
				if (invoiceInputRef.current) invoiceInputRef.current.value = '';
			}

			onChanged({ draft });
		} catch (err) {
			onError(err instanceof Error ? err.message : undefined);
		} finally {
			setIsSaving(false);
		}
	};

	// Surface a dollar figure on every milestone — if the condition only
	// stores a percentage, derive it from the contract commitment so the
	// supplier always sees the cash value. When a percentage is set we keep
	// it as its own chip alongside the amount.
	const pct =
		milestone.paymentPercentage != null && milestone.paymentPercentage > 0
			? milestone.paymentPercentage
			: null;
	// Base the derived cash value on the contract commitment when present,
	// otherwise fall back to the contract amount recorded in this milestone's
	// currency — contracts can carry several currency lines and often have no
	// single commitment figure.
	const contractBaseAmount =
		contract?.commitmentAmount != null
			? contract.commitmentAmount
			: (contract?.amounts || []).find((a) => a.currency === currency)?.amount ?? null;
	const amountValue =
		milestone.paymentAmount != null && milestone.paymentAmount > 0
			? milestone.paymentAmount
			: pct != null && contractBaseAmount != null
				? contractBaseAmount * (pct / 100)
				: null;
	const amountLabel = amountValue != null ? formatCurrency({ currency, value: amountValue }) : '';
	const percentLabel = pct != null ? `${pct}%` : '';

	const supplierName = useMemo(
		() =>
			(contract.entities || []).find((e) => (e.type || '').toUpperCase() === 'SUPPLIER')?.name ||
			'',
		[contract.entities]
	);

	// Invoices already submitted against this milestone. The contract-by-id
	// response now ships each milestone's linked invoice(s) inline (matched
	// server-side by invoice.milestoneNumber === condition.id, the milestone's
	// stable primary key), so prefer that; fall back to the contract-wide
	// `invoices` prop for any caller that hasn't adopted the embedded shape yet.
	const milestoneInvoices = useMemo(
		() =>
			milestone.invoices ??
			(invoices || []).filter((inv) => inv.milestoneNumber === milestone.id),
		[milestone.invoices, invoices, milestone.id]
	);

	// Resolve an invoice attachment to a viewable file URL by matching its
	// AI document id to an already-fetched document's blob_url.
	const resolveFileUrl = (att: InvoiceAttachment): string | undefined =>
		att.id != null ? blobUrlByDocId?.get(String(att.id)) : undefined;

	const statusLabel = isPaid ? t('paid') : formatStatusLabel(milestone.status);
	const statusColor: 'success' | 'default' =
		isPaid || milestone.status === 'VERIFIED' ? 'success' : 'default';

	// Supplier-side submission state derived from the linked voucher's
	// section_responses. Surfaced as a tri-state pill so the supplier can
	// see whether their entries are still local-draft, saved as DRAFT on
	// the server, or handed to PIU (SUBMITTED). PIU verification states
	// (VERIFIED/REJECTED) flow through `statusLabel` above and don't need
	// to be duplicated here.
	type SupplierSubmissionState = 'NONE' | 'PENDING' | 'SUBMITTED';
	const supplierState: SupplierSubmissionState = useMemo(() => {
		const sr = linkedVouchers
			.map((v) => (v as Voucher & { sectionResponses?: SectionResponse[] }).sectionResponses)
			.find((s): s is SectionResponse[] => Array.isArray(s) && s.length > 0);
		if (!sr) return 'NONE';
		if (sr.some((s) => s.status === 'SUBMITTED')) return 'SUBMITTED';
		if (sr.some((s) => s.status === 'PENDING')) return 'PENDING';
		return 'NONE';
	}, [linkedVouchers]);
	const supplierStateChip: { label: string; color: 'default' | 'info' | 'success' } | null =
		supplierState === 'SUBMITTED'
			? { label: t('submittedToPiu'), color: 'success' }
			: supplierState === 'PENDING'
				? { label: t('draftSaved'), color: 'info' }
				: null;

	// When this milestone was last handed to the PIU (or last draft-saved),
	// read from the linked voucher's section responses. The submitter's name
	// lives on the PIU side (resolved from the user id); the supplier just sees
	// the timestamp of their own action.
	const supplierSubmittedAt = useMemo(() => {
		const sr = linkedVouchers
			.map((v) => (v as Voucher & { sectionResponses?: SectionResponse[] }).sectionResponses)
			.find((s): s is SectionResponse[] => Array.isArray(s) && s.length > 0);
		if (!sr) return null;
		const latest = sr
			.filter((s) => s.submittedAt)
			.sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''))[0];
		return latest?.submittedAt ?? null;
	}, [linkedVouchers]);

	// Settlement date for the paid pill subtitle — drawn from the REDEEMED
	// voucher's dateProcessed when present.
	const paidVoucher = isPaid
		? linkedVouchers.find((v) => (v.status || '').toUpperCase() === 'REDEEMED')
		: undefined;
	const paidOn = paidVoucher?.dateProcessed
		? formatDate({ date: new Date(paidVoucher.dateProcessed) })
		: null;

	// The whole card surfaces interactivity through MUI `disabled`: paid /
	// locked / sequencing-gated / contract-not-active all converge on the
	// same visual treatment (greyed inputs, no upload, no submit). Read-only
	// stays separate only for the document renderer, which uses it to hide
	// the Replace button. This mirrors the user's directive: requirements
	// stay visible no matter what; what changes is whether you can touch them.
	const fieldsLocked = disabled || readOnly;

	return (
		<Box
			sx={{
				py: 2,
				px: { xs: 2, sm: 3 },
				mx: { xs: -2, sm: -3 },
				bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
				'&:first-of-type': { pt: 0 },
				'&:last-of-type': { pb: 0 },
			}}>
			<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap">
				<Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
					{index + 1}. {milestone.name}
				</Typography>
				{amountLabel && <Chip label={amountLabel} size="small" variant="outlined" />}
				{percentLabel && <Chip label={percentLabel} size="small" variant="outlined" />}
				{paidOn && (
					<Typography variant="caption" color="text.secondary">
						{paidOn}
					</Typography>
				)}
				{milestone.isRequired && !isPaid && supplierState === 'NONE' && (
					<Chip label={t('required')} size="small" variant="outlined" color="warning" />
				)}
				{!isPaid && supplierStateChip && (
					<Chip
						color={supplierStateChip.color}
						label={supplierStateChip.label}
						size="small"
						variant="outlined"
					/>
				)}
				{isPaid ? (
					<Chip color="success" label={t('paid')} size="small" />
				) : readOnly ? (
					<Chip icon={<LockIcon />} label={t('milestoneLocked')} size="small" />
				) : (
					statusLabel &&
					milestone.status !== 'PENDING' && (
						<Chip color={statusColor} label={statusLabel} size="small" variant="outlined" />
					)
				)}
			</Stack>

			{milestone.description && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
					{milestone.description}
				</Typography>
			)}

			{disabled && !isPaid && (
				<Alert severity="info" sx={{ mb: 2 }}>
					{t('completePreviousMilestoneFirst')}
				</Alert>
			)}

			{sections.length === 0 ? (
				<Alert severity="info" sx={{ mb: 2 }}>
					{t('noSupplierFields')}
				</Alert>
			) : (
				<Stack spacing={2.5} sx={{ mb: 2 }}>
					{sections.map((section, sIdx) => {
						const supplierFields = section.fields.filter((f) => f.allowSupplierCompletion === true);
						const internalOnly = section.fields.filter((f) => f.allowSupplierCompletion !== true);
						return (
							<Box
								key={section.id}
								sx={{
									border: '1px solid',
									borderColor: 'divider',
									borderRadius: 1,
									bgcolor: 'background.default',
									overflow: 'hidden',
								}}>
								<Stack
									direction="row"
									spacing={0.75}
									alignItems="center"
									sx={{
										px: 1.5,
										py: 1,
										bgcolor: 'action.hover',
										borderBottom: '1px solid',
										borderColor: 'divider',
									}}>
									<GroupIcon sx={{ fontSize: 20, color: 'primary.main' }} />
									<Typography
										sx={{
											fontSize: '1.25rem',
											fontWeight: 700,
											color: 'text.primary',
											flex: 1,
										}}>
										{section.name || `Section ${sIdx + 1}`}
									</Typography>
									{section.source === 'TEMPLATE' && section.sourceConditionName && (
										<Chip
											label={section.sourceConditionName}
											size="small"
											variant="outlined"
											color="secondary"
										/>
									)}
									{section.fields.length > 0 && (
										<Typography
											sx={{
												fontSize: '1.25rem',
												fontWeight: 600,
												color: 'text.secondary',
											}}>
											{section.fields.length} requirement
											{section.fields.length === 1 ? '' : 's'}
										</Typography>
									)}
								</Stack>
								<Box sx={{ p: 1.5 }}>
									{supplierFields.length === 0 && internalOnly.length === 0 && (
										<Typography variant="caption" color="text.secondary">
											{t('noSupplierFields')}
										</Typography>
									)}
									<Stack spacing={2}>
										{supplierFields.map((field) => {
											const state = fieldState.get(fieldKey(section.id, field.id)) || {
												value: '',
												documentIds: [],
												documentNames: [],
											};
											const errKey = fieldKey(section.id, field.id);
											const error = fieldErrors.get(errKey) || null;
											const value = state.value;
											const disabled = fieldsLocked || isSaving;
											const onValueChange = (v: unknown) =>
												updateField(section.id, field.id, { value: v });

											// Standard scalar inputs render through the shared Input/Select
											// components directly — they are not custom field types, so they
											// do not belong in the FIELD_RENDERERS registry.
											if (
												field.fieldType === 'text' ||
												field.fieldType === 'number' ||
												field.fieldType === 'date' ||
												field.fieldType === 'select'
											) {
												return (
													<Box key={field.id}>
														<Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
															{field.label}
															{field.required === true ? ' *' : ''}
														</Typography>
														{field.fieldType === 'select' ? (
															<Select
																options={(field.options || []).map((opt) => ({
																	label: opt,
																	value: opt,
																}))}
																disabled={disabled}
																readOnly={readOnly}
																required={field.required === true}
																value={value == null ? '' : String(value)}
																onChange={(e) => onValueChange(e.target.value)}
																error={!!error}
																helperText={error || undefined}
															/>
														) : (
															<Input
																type={field.fieldType === 'text' ? undefined : field.fieldType}
																currency={field.fieldType === 'number' ? field.unit : undefined}
																placeholder={field.placeholder || ''}
																disabled={disabled}
																value={value == null ? '' : String(value)}
																onChange={(e) => onValueChange(e.target.value)}
																InputProps={{ readOnly }}
																error={!!error}
																helperText={error || undefined}
															/>
														)}
													</Box>
												);
											}

											if (field.fieldType === 'yesno') {
												return (
													<YesNo
														key={field.id}
														field={field}
														value={value}
														onChange={onValueChange}
														disabled={disabled}
														readOnly={readOnly}
														error={error}
													/>
												);
											}
											return (
												<UploadDoc
													key={field.id}
													field={field}
													disabled={disabled}
													readOnly={readOnly}
													error={error}
													uploadedDocumentIds={state.documentIds}
													uploadedDocumentNames={state.documentNames}
													onUpload={async (file) => {
														const res = await uploadFieldDocument(
															file,
															contract.id,
															contract.project,
															supplierEntityId,
															`field:${field.id}`,
															{
																conditionId: milestone.id,
																sectionId: section.id,
																fieldId: field.id,
															}
														);
														updateField(section.id, field.id, {
															documentIds:
																field.maxDocuments === 1
																	? [res.documentId]
																	: [...state.documentIds, res.documentId],
															documentNames:
																field.maxDocuments === 1
																	? [res.documentName]
																	: [...state.documentNames, res.documentName],
														});
														return res;
													}}
													onRemoveDocument={(id) => {
														const ids = state.documentIds.filter((x) => x !== id);
														const names = state.documentNames.filter(
															(_, i) => state.documentIds[i] !== id
														);
														updateField(section.id, field.id, {
															documentIds: ids,
															documentNames: names,
														});
													}}
													documents={documents}
													onSelectExisting={(picked) => {
														if (field.maxDocuments === 1) {
															const p = picked[0];
															updateField(section.id, field.id, {
																documentIds: p ? [p.documentId] : [],
																documentNames: p ? [p.documentName] : [],
															});
															return;
														}
														const ids = [...state.documentIds];
														const names = [...state.documentNames];
														picked.forEach((p) => {
															if (!ids.includes(p.documentId)) {
																ids.push(p.documentId);
																names.push(p.documentName);
															}
														});
														updateField(section.id, field.id, {
															documentIds: ids,
															documentNames: names,
														});
													}}
												/>
											);
										})}
										{internalOnly.map((field) => (
											<Stack
												key={field.id}
												direction="row"
												spacing={1}
												alignItems="center"
												sx={{ opacity: 0.6 }}>
												<LockIcon fontSize="small" />
												<Typography variant="body2" color="text.secondary">
													{field.label}
												</Typography>
												<Chip label={t('internalOnlyField')} size="small" variant="outlined" />
											</Stack>
										))}
									</Stack>
								</Box>
							</Box>
						);
					})}
				</Stack>
			)}

			{((!readOnly && !disabled) || milestoneInvoices.length > 0) && (
				<Box
					sx={{
						border: '1px solid',
						borderColor: 'divider',
						borderRadius: 1,
						bgcolor: 'background.default',
						overflow: 'hidden',
					}}>
					{milestoneInvoices.length > 0 ? (
						// Submitted invoice(s) rendered flat so they read as this
						// section's content, not a nested card-in-card.
						milestoneInvoices.map((inv) => (
							<CardInvoiceDetails key={inv.id} invoice={inv} resolveFileUrl={resolveFileUrl} flat />
						))
					) : (
						<Stack
							direction="row"
							spacing={0.75}
							alignItems="center"
							sx={{
								px: 1.5,
								py: 1,
								bgcolor: 'action.hover',
								borderBottom: '1px solid',
								borderColor: 'divider',
							}}>
							<ReceiptLongIcon sx={{ fontSize: 20, color: 'primary.main' }} />
							<Typography
								sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'text.primary', flex: 1 }}>
								{t('invoiceForMilestone')}
							</Typography>
						</Stack>
					)}
					{!readOnly && !disabled && (
						<>
							{milestoneInvoices.length > 0 && <Divider />}
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
								sx={{ p: 1.5, flexWrap: 'wrap' }}>
								<input
									ref={invoiceInputRef}
									type="file"
									hidden
									accept=".csv, .doc, .docx, image/*, .pdf, .pptx, .rtf, .txt, .webp, .xls, .xlsx, .xml"
									onChange={(e) =>
										setInvoiceFiles(e.target.files ? Array.from(e.target.files) : [])
									}
								/>
								<Button
									size="small"
									variant="outlined"
									startIcon={<UploadFileIcon />}
									disabled={isSaving}
									onClick={() => invoiceInputRef.current?.click()}>
									{t('browseFiles')}
								</Button>
								{invoiceFiles[0] ? (
									<Chip
										label={invoiceFiles[0].name}
										size="small"
										onDelete={isSaving ? undefined : () => setInvoiceFiles([])}
										sx={{ maxWidth: 280 }}
									/>
								) : (
									<Typography variant="body2" color="text.secondary">
										{t('noFileSelected')}
									</Typography>
								)}
							</Stack>
							{invoiceFiles[0] && (
								<Stack spacing={1.5} sx={{ px: 1.5, pb: 1.5 }}>
									<Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
										<Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
											<Input
												label={t('number')}
												value={invoiceNumber}
												onChange={(e) => setInvoiceNumber(e.target.value)}
												required
												disabled={isSaving}
												error={invoiceErrors.number}
												helperText={invoiceErrors.number ? t('requiredField') : undefined}
											/>
										</Box>
										<Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
											<Input
												type="date"
												label={t('invoiceDate')}
												value={invoiceDate}
												onChange={(e) => setInvoiceDate(e.target.value)}
												required
												disabled={isSaving}
												error={invoiceErrors.date}
												helperText={invoiceErrors.date ? t('requiredField') : undefined}
											/>
										</Box>
									</Stack>
									<Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
										<Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
											<Input
												type="number"
												label={t('amount')}
												value={invoiceAmount}
												onChange={(e) => setInvoiceAmount(e.target.value)}
												required
												disabled={isSaving}
												error={invoiceErrors.amount}
												helperText={invoiceErrors.amount ? t('requiredField') : undefined}
											/>
										</Box>
										<Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
											<SelectCurrency
												label={t('currency')}
												currencies={Array.from(
													new Set(
														[currency, ...(contract.amounts || []).map((a) => a.currency)].filter(
															Boolean
														)
													)
												)}
												value={invoiceCurrency}
												onChange={(e) => setInvoiceCurrency(String(e.target.value))}
												disabled={isSaving}
											/>
										</Box>
									</Stack>
								</Stack>
							)}
						</>
					)}
				</Box>
			)}

			{!readOnly && !disabled && (
				<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 2 }}>
					{hasSupplierActionableFields && (
						<Tooltip title={supplierState === 'PENDING' ? t('saveDraft') : ''}>
							<span>
								<Button
									size="small"
									variant="outlined"
									disabled={isSaving}
									sx={{ minWidth: 150 }}
									startIcon={supplierState === 'PENDING' ? <CheckIcon /> : <SaveOutlinedIcon />}
									onClick={() => doSubmit(true)}>
									{supplierState === 'PENDING' ? t('saved') : t('saveDraft')}
								</Button>
							</span>
						</Tooltip>
					)}
					<Tooltip title={supplierState === 'SUBMITTED' ? t('submitMilestone') : ''}>
						<span>
							<Button
								size="small"
								variant="contained"
								disabled={isSaving || (!hasSupplierActionableFields && invoiceFiles.length === 0)}
								sx={{ minWidth: 150 }}
								startIcon={supplierState === 'SUBMITTED' ? <CheckIcon /> : <SendOutlinedIcon />}
								onClick={() => doSubmit(false)}>
								{supplierState === 'SUBMITTED' ? t('submittedToPiu') : t('submitMilestone')}
							</Button>
						</span>
					</Tooltip>
					{supplierSubmittedAt && (
						<Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
							{formatDate({ date: new Date(supplierSubmittedAt) })}
						</Typography>
					)}
				</Stack>
			)}
		</Box>
	);
};
